import { currencyFormatter, numberFormatter } from '@root/helpers/formatters';
import { escape } from 'lodash-es';

import type { ScheduleItemData } from './types';

// The mention token is always a <span> (see helpers/editor/create-mention.ts) — every nested
// element here must stay inline-safe (<span>, never <div>/<p>), or the parser hoists block
// children out of the mention span and the content renders unstyled outside it.

const formatScheduleItemValue = (item: ScheduleItemData): string | undefined => {
  switch (item.value_type) {
    case 'Amount': {
      // Either half of an amount can be missing while the broker is still filling the form, and
      // whichever half they HAVE entered is shown — a currency on its own, or a number on its own.
      // currencyFormatter is used only once a currency is actually set: left to itself it falls
      // back to USD, which would print a currency nobody chose into a contract document.
      const { amount, currency } = item.value_amount ?? {};
      const hasAmount = amount !== undefined && amount !== null;
      const formatted = hasAmount
        ? currency
          ? currencyFormatter({ amount, currency })
          : numberFormatter(amount)
        : false;

      return formatted || currency || undefined;
    }
    case 'Percentage': {
      const percentage = item.value_percentage;

      return percentage === undefined || percentage === null ? undefined : `${percentage}%`;
    }
    case 'Timeframe': {
      // Same rule as Amount above — whichever half was entered is shown. Filtering on truthiness
      // would drop a value of 0, which the schema and the editor both allow, leaving a bare unit.
      const { value, unit } = item.value_timeframe ?? {};
      const parts = [value, unit].filter((part) => part !== undefined && part !== null && String(part).trim() !== '');

      return parts.length > 0 ? parts.join(' ') : undefined;
    }
    case 'Other':
      return item.value_type_other || undefined;
    default:
      return undefined;
  }
};

const formatScheduleItemBasis = (item: ScheduleItemData): string | undefined => {
  if (!item.basis?.length) return undefined;

  const parts = item.basis
    .map((entry) => (entry === 'Other' ? item.basis_other : entry))
    .filter((part) => part !== undefined && part !== null && String(part).trim() !== '');

  return parts.length > 0 ? parts.join(', ') : undefined;
};

const renderScheduleItem = (item: ScheduleItemData): string => {
  const value = formatScheduleItemValue(item);
  const basis = formatScheduleItemBasis(item);
  const children = (item.children ?? []).map(renderScheduleItem).join('');

  // Every interpolated value is broker-entered free text, so it is escaped on the way in. The SPA
  // path runs the result through sanitizeHtml (replace-mention.ts) but the PDF path assigns it to
  // innerHTML directly (replaceContractVariables.ts), and either way an unescaped `<` or `&` in a
  // title corrupts the surrounding block. Escaping here makes the output correct for both callers.
  // <strong>, not a font-weight class, matches how bold already works elsewhere in block content
  // (e.g. existing table cells use literal <strong> rather than relying on CSS specificity).
  const valueContent = value ? `<strong>${escape(value)}</strong>` : '';
  const titleCellContent =
    (item.title ? `<span class="schedule-mention-item-title"><strong>${escape(item.title)}</strong></span>` : '') +
    (basis ? `<span class="schedule-mention-item-basis">${escape(basis)}</span>` : '') +
    (item.description ? `<span class="schedule-mention-item-description">${escape(item.description)}</span>` : '');

  // A CSS table-display row (see contract-mentions.css): value is one cell, title+basis+description
  // share the second cell so basis/description align under the title, not under the value.
  // BOTH cells are emitted whenever the row renders, even when the value is empty: the layout is
  // `table-layout: fixed`, so the first cell in the row takes the value column's width. Dropping an
  // empty value cell promoted the title into that column, which left items with no value hanging
  // under the values of their siblings instead of lining up with their titles.
  // Kept whitespace-free between tags: `.mention-data` sets `white-space: pre-line`, so any
  // literal newline/indentation here would render as a visible blank line.
  return (
    '<span class="schedule-mention-item">' +
    (valueContent || titleCellContent
      ? '<span class="schedule-mention-item-row">' +
        `<span class="schedule-mention-item-value">${valueContent}</span>` +
        `<span class="schedule-mention-item-title-cell">${titleCellContent}</span>` +
        '</span>'
      : '') +
    (children ? `<span class="schedule-mention-item-children">${children}</span>` : '') +
    '</span>'
  );
};

export const renderScheduleMentionHtml = (items: ScheduleItemData[] = []): string =>
  items.map(renderScheduleItem).join('');
